import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../client";
import type { Contact, ContactCreate, ContactUpdate } from "../types";

const contactsKey = (siteId: string) => ["sites", siteId, "contacts"] as const;

export function useContacts(siteId: string | undefined) {
  return useQuery<Contact[]>({
    queryKey: siteId ? contactsKey(siteId) : ["contacts", "disabled"],
    queryFn: () => apiRequest(`/api/sites/${siteId}/contacts`),
    enabled: Boolean(siteId),
  });
}

export function useCreateContact(siteId: string) {
  const qc = useQueryClient();
  return useMutation<Contact, Error, ContactCreate>({
    mutationFn: (payload) =>
      apiRequest(`/api/sites/${siteId}/contacts`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: contactsKey(siteId) }),
  });
}

export function useUpdateContact(siteId: string, contactId: string) {
  const qc = useQueryClient();
  return useMutation<Contact, Error, ContactUpdate>({
    mutationFn: (payload) =>
      apiRequest(`/api/sites/${siteId}/contacts/${contactId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: contactsKey(siteId) }),
  });
}

export function useDeleteContact(siteId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (contactId) =>
      apiRequest(`/api/sites/${siteId}/contacts/${contactId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: contactsKey(siteId) }),
  });
}
